import { defineStore } from "pinia";
import GeoJSON, { Feature, FeatureCollection, Point } from "geojson";
import mapboxgl, { LngLat } from "mapbox-gl";
import {
  featureCollection as tFeatureCollection,
  lineString as tLineString,
  point as tPoint,
} from "@turf/helpers";
import { length as tLength } from "@turf/turf";
import { v4 as uuid } from "uuid";
import { emptyGeoJSON } from "@/util/helpers";
import { useMap } from ".";

interface MeasuringToolState {
  measurementPoints: Map<string, LngLat>;
  pointsLayerName: string;
  linesLayerName: string;
  activePoint: string | null;
}

export const useMeasuringTool = defineStore("measuringTool", {
  state: (): MeasuringToolState => ({
    measurementPoints: new Map<string, LngLat>(),
    pointsLayerName: "map-measurements-points",
    linesLayerName: "map-measurements",
    activePoint: null,
  }),
  actions: {
    setPoint(coords: LngLat, id?: string): void {
      this.measurementPoints.set(id || uuid(), coords);
      this.drawMeasurements();
    },
    removePoint(id: string): void {
      this.measurementPoints.delete(id);

      if (this.measurementPoints.size === 0) {
        this.clearMeasurements();
      }

      this.drawMeasurements();
    },
    drawMeasurements(): void {
      if (this.measurementPoints.size < 1) {
        return;
      }

      const points = Array.from(this.measurementPoints.keys()).map((id) => {
        const lngLat = this.measurementPoints.get(id) as mapboxgl.LngLat;
        return tPoint([lngLat.lng, lngLat.lat], { id, distance: "" });
      });

      // draw points
      this.addMeasurementPoints(points);

      // draw lines
      if (this.measurementPoints.size > 1) {
        this.addMeasurementLine(points.map((x) => x.geometry.coordinates));
      } else {
        this.setSourceData(this.linesLayerName, emptyGeoJSON);
      }
    },
    calcLength(
      geojson: Feature<any, any> | FeatureCollection<any, any>
    ): string {
      const length = tLength(geojson);
      return length < 1
        ? Math.round((length * 100000) / 100) + "m"
        : Math.round((length * 100) / 100) + "km";
    },
    setSourceData(
      sourceName: string,
      data: GeoJSON.FeatureCollection<
        GeoJSON.Geometry,
        GeoJSON.GeoJsonProperties
      >
    ) {
      (useMap().map?.getSource(sourceName) as mapboxgl.GeoJSONSource).setData(
        data
      );
    },
    addMeasurementPoints(points: Feature<Point, any>[]) {
      // adds total distance to the last point
      if (points.length > 2) {
        const distance = this.calcLength(
          tLineString(points.map((x) => x.geometry.coordinates))
        );
        points[points.length - 1].properties.distance = `Total: ${distance}`;
      }
      this.setSourceData(this.pointsLayerName, tFeatureCollection(points));
    },
    addMeasurementLine(coordinates: GeoJSON.Position[]): void {
      const lines = coordinates.slice(1).map((x, i) => {
        const line = tLineString([coordinates[i], x]);
        line.properties = {
          id: uuid(),
          distance: this.calcLength(line),
        };
        return line;
      });

      this.setSourceData(this.linesLayerName, {
        type: "FeatureCollection",
        features: lines,
      });
    },
    clearMeasurements(): void {
      this.measurementPoints.clear();
      this.measurementPoints = new Map();
      this.activePoint = null;
      this.setSourceData(this.linesLayerName, emptyGeoJSON);
      this.setSourceData(this.pointsLayerName, emptyGeoJSON);
    },
    initMeasuringTool(map: mapboxgl.Map): any {
      const pointsLayerName = "map-measurements-points";

      map.addSource(this.linesLayerName, {
        type: "geojson",
        data: emptyGeoJSON,
      });

      map.addSource(pointsLayerName, {
        type: "geojson",
        data: emptyGeoJSON,
      });

      map.addLayer({
        id: "map-measurements-line-outline",
        type: "line",
        source: this.linesLayerName,
        filter: ["==", "$type", "LineString"],
        paint: {
          "line-color": "#F2F2F0",
          "line-width": 4,
        },
      });

      map.addLayer({
        id: "map-measurements-line",
        type: "line",
        source: this.linesLayerName,
        filter: ["==", "$type", "LineString"],
        paint: {
          "line-color": "#626262",
          "line-width": 3,
        },
      });

      map.addLayer({
        id: "map-measurements-line-distance",
        type: "symbol",
        source: this.linesLayerName,
        filter: ["==", "$type", "LineString"],
        layout: {
          "text-field": "{distance}",
          "text-font": ["Source Sans Pro Regular"],
          "text-size": 18,
          "symbol-placement": "line-center",
        },
        paint: {
          "text-color": "#626262",
          "text-halo-color": "#F2F2F0",
          "text-halo-blur": 0,
          "text-halo-width": 10,
        },
      });

      map.addLayer({
        id: "map-measurements-distance",
        type: "symbol",
        source: this.pointsLayerName,
        filter: ["==", "$type", "Point"],
        layout: {
          "text-field": "{distance}",
          "text-font": ["Source Sans Pro Regular"],
          "text-size": 18,
          "symbol-placement": "point",
          "text-anchor": "top",
          "text-offset": [0, 0.5],
        },
        paint: {
          "text-color": "#626262",
          "text-halo-color": "#F2F2F0",
          "text-halo-blur": 0,
          "text-halo-width": 10,
        },
      });

      map.addLayer({
        id: "point",
        type: "circle",
        source: pointsLayerName,
        paint: {
          "circle-radius": 5,
          "circle-color": "white",
          "circle-stroke-width": 2,
          "circle-stroke-color": "#626262",
        },
      });

      // measuring specific interaction
      map.on("mousedown", "point", (e) => this.mouseDown(e));
      map.on("mousemove", "point", (e) => this.mouseMove(e));
      map.on("click", (e) => this.mouseClick(e));
      map.on(
        "mouseenter",
        "point",
        () => (map.getCanvas().style.cursor = "pointer")
      );
      map.on("mouseleave", "point", () => (map.getCanvas().style.cursor = ""));

      // first draw
      this.drawMeasurements();
    },
    mouseClick(e: mapboxgl.MapMouseEvent) {
      const map = useMap().map;
      if (!this.measurementPoints.size || !map) {
        return;
      }

      const pointFeature = map
        .queryRenderedFeatures(e.point)
        .find((x) => x.source === this.pointsLayerName);

      if (pointFeature) {
        this.removePoint(pointFeature.properties?.id);
      } else {
        this.setPoint(e.lngLat);
      }
    },
    mouseDown(e: mapboxgl.MapMouseEvent) {
      e.preventDefault();
      const map = useMap().map;
      if (!map) {
        return;
      }

      const pointFeature = map
        .queryRenderedFeatures(e.point)
        .find((x) => x.source === this.pointsLayerName);

      if (!pointFeature) {
        return;
      }

      this.activePoint = pointFeature.properties?.id;
      map.on("mousemove", this.mouseMove);
      map.once("mouseup", this.mouseUp);
    },
    mouseMove(e: mapboxgl.MapMouseEvent) {
      if (!this.activePoint || !this.measurementPoints.has(this.activePoint)) {
        return;
      }

      this.setPoint(e.lngLat, this.activePoint);
    },
    mouseUp() {
      const map = useMap().map;
      if (!map) {
        return;
      }

      map.off("mousemove", this.mouseMove);
      this.activePoint = null;
    },
  },
});
